import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';

/**
 * Family Login Seite - Hayat Design System
 * Premium Dark Theme mit Glassmorphism
 */
function FamilyLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useFamilyAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      console.log('✅ Login successful, redirecting...');
      navigate('/family/dashboard');
    } catch (err) {
      console.error('❌ Login failed:', err);
      setError(err.message || 'Login fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816] flex items-center justify-center p-4">
      {/* Login Card - Hayat Brand Style */}
      <div className="w-full max-w-md">
        {/* Logo & Header */}
        <div className="text-center mb-8 animate-fade-in">
          {/* Logo Container */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#5B4B8A] to-[#4a3d72] mb-6 shadow-2xl">
            <span className="text-4xl">💙</span>
          </div>
          
          {/* Brand Title */}
          <h1 className="text-4xl font-bold text-[#F9FAFB] mb-2">
            HAYAT
          </h1>
          <p className="text-lg text-[#6DCEE8] font-medium">
            Family
          </p>
          <p className="text-sm text-[#CBD5F5] mt-1">
            Für Angehörige
          </p>
        </div>

        {/* Login Form Card */}
        <div className="bg-neutral-900/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-neutral-600/40 animate-scale-in">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
                E-Mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ihre@email.de"
                required
                className="w-full px-4 py-3 bg-neutral-800/50 border-2 border-neutral-600/40 text-[#F9FAFB] placeholder-neutral-500 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#5B4B8A]/50 focus:border-[#5B4B8A] transition-all"
                disabled={loading}
              />
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-sm font-bold text-[#F9FAFB] mb-2">
                Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full px-4 py-3 bg-neutral-800/50 border-2 border-neutral-600/40 text-[#F9FAFB] placeholder-neutral-500 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#5B4B8A]/50 focus:border-[#5B4B8A] transition-all"
                disabled={loading}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#EF4444] px-4 py-3 rounded-xl animate-fade-in">
                <span className="font-semibold">⚠️ {error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button 
              type="submit" 
              className="w-full py-4 bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed min-h-[56px]"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Wird angemeldet...
                </span>
              ) : (
                '🔐 Anmelden'
              )}
            </button>
          </form>

          {/* Phase 18: New User Onboarding Button */}
          <div className="mt-6 pt-6 border-t border-neutral-600/40">
            <p className="text-sm text-[#CBD5F5] mb-3 font-semibold text-center">
              Noch kein Konto?
            </p>
            <button
              type="button"
              onClick={() => navigate('/family/onboarding/start')}
              className="w-full py-3 bg-gradient-to-r from-[#6DCEE8] to-[#5ab4cc] text-[#050816] rounded-xl font-bold text-base shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300"
            >
              🚀 Neues System einrichten
            </button>
          </div>

          {/* Demo Hint */}
          <div className="mt-4 pt-4 border-t border-neutral-600/40">
            <p className="text-sm text-[#CBD5F5] mb-3 font-semibold">
              💡 Demo-Login:
            </p>
            <div className="space-y-2 text-xs text-neutral-500 bg-neutral-800/30 rounded-xl p-3">
              <p className="font-mono">mehmet@familie-eren.de / test123</p>
              <p className="font-mono">ayse@familie-eren.de / test123</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-neutral-500 mt-6">
          Hayat © 2025 · Powered by Emergent
        </p>
      </div>
    </div>
  );
}

export default FamilyLogin;
